import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mountain, ArrowLeft } from 'lucide-react';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('ログインしました');
      } else {
        await register(email, password, name);
        toast.success('アカウント作成完了');
      }
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-bg flex flex-col items-center justify-center px-6" data-testid="auth-page">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 size-10 rounded-full glass-panel flex items-center justify-center text-zinc-400 hover:text-white transition-colors z-10"
        data-testid="auth-back-btn"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="w-full max-w-sm space-y-8 animate-slide-up">
        {/* Logo */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center size-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 mb-2">
            <Mountain size={28} className="text-orange-500" />
          </div>
          <h1
            className="text-4xl font-bold tracking-tight uppercase"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            TrailBlaze
          </h1>
          <p className="text-sm text-zinc-400">
            {mode === 'login' ? 'アカウントにログイン' : '新しいアカウントを作成'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">名前</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田太郎"
                required
                className="bg-white/5 border-white/10 h-12 rounded-xl"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">メール</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="bg-white/5 border-white/10 h-12 rounded-xl"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">パスワード</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6文字以上"
              required
              minLength={6}
              className="bg-white/5 border-white/10 h-12 rounded-xl"
              data-testid="auth-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base tracking-wide"
            data-testid="auth-submit-btn"
          >
            {loading ? '処理中...' : mode === 'login' ? 'ログイン' : '登録'}
          </Button>
        </form>

        {/* Toggle */}
        <div className="text-center">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-sm text-zinc-400 hover:text-orange-400 transition-colors"
            data-testid="auth-toggle-btn"
          >
            {mode === 'login' ? 'アカウントをお持ちでない方はこちら' : 'すでにアカウントをお持ちの方'}
          </button>
        </div>
      </div>
    </div>
  );
}
